import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import Toggle from 'components/ReusableComponents/Toggle'
import OTABookingsList from 'features/MonthlyOTAConversions/OTABookingsList'
import { useInternalAdmin } from 'hooks'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'
import canadianProvinces from 'static/addresses/canadianProvinces'
import usStates from 'static/addresses/usStates'
import { formatNumberAsThousands } from 'utils/priceHelpers'

import styles from './MonthlyOTAConversions.module.scss'
import RevenueTooltip from './RevenueTooltip'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0
})

const colors = {
  revenue: '#3AAF71',
  bookings: '#667EEA'
}

const MonthlyOTAConversions = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)

  const [activeYear, setActiveYear] = useState(moment().format('YYYY'))
  const [activeMonth, setActiveMonth] = useState()

  const [stateToFilter, setStateToFilter] = useState('')
  const [cityToFilter, setCityToFilter] = useState('')

  const isInternalAdmin = useInternalAdmin()

  const [hotelGroups, setHotelGroups] = useState()

  const [groupToFilter, setGroupToFilter] = useState()

  const [availableCities, setAvailableCities] = useState()

  const [response, setResponse] = useState()

  const [showAllHotels, setShowAllHotels] = useState(false)
  const [showWholeGroup, setShowWholeGroup] = useState(false)
  const [parentChain, setParentChain] = useState()

  const [isOtaOnly, setIsOTAOnly] = useState(true)

  let kind = 'CurrentSubsidiary'

  if (showWholeGroup) {
    kind = 'WholeGroup'
  }

  if (showAllHotels) {
    kind = 'AllSubsidiaries'
  }

  useEffect(() => {
    setActiveMonth(null)
    performanceReportsApi
      .monthlyOTAConversions({
        isOtaOnly,
        year: activeYear,
        kind,
        parentChainCode: parentChain,
        city: cityToFilter,
        groupId: groupToFilter,
        state: stateToFilter
      })
      .then(res => {
        setResponse(res)
        setAvailableCities(res.cities)
        setHotelGroups(
          res.hotelGroups.reduce((acc, hotelGroup) => {
            return { ...acc, [hotelGroup.id]: hotelGroup.name }
          }, {})
        )
        setIsErred(false)
      })
      .catch(() => setIsErred(true))
      .finally(() => setIsLoading(false))
  }, [
    activeYear,
    showWholeGroup,
    showAllHotels,
    parentChain,
    cityToFilter,
    stateToFilter,
    groupToFilter,
    kind,
    isOtaOnly
  ])

  const ref = useRef()

  function handleBarClick(data) {
    if (data?.activePayload?.length) {
      setActiveMonth(data.activePayload[0].payload.month)
    } else {
      setActiveMonth(null)
    }
  }

  if (isLoading) {
    return <LoadingIndicator />
  }

  if (isErred) {
    return <p>Something went wrong</p>
  }

  const years = Array(
    Number(moment().format('YYYY')) -
      Number(moment(response?.serviceStartDate || 2018).format('YYYY')) +
      1
  )
    .fill(0)
    .map((_, i) => Number(moment().format('YYYY')) - i)

  function handleChangeState(state) {
    setStateToFilter(state)
    setCityToFilter('')
  }

  function handleChangeGroupFilter(value) {
    if (value === 'currentHotel') {
      setShowWholeGroup(false)
      setShowAllHotels(false)
      setGroupToFilter()
    }

    if (value === 'allHotels') {
      setShowWholeGroup(false)
      setShowAllHotels(true)
      setGroupToFilter()
    }

    if (value !== 'currentHotel' && value !== 'allHotels') {
      setGroupToFilter(value)
      setShowAllHotels(true)
      setShowWholeGroup(false)
    }
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Monthly OTA conversions" width={1080} />
      <div className={styles.topBar}>
        {!showAllHotels && (
          <p className={styles.serviceStartDate}>
            <InfoIcon />
            Directful service launched on{' '}
            {moment(response.serviceStartDate).format('MMMM Do, YYYY')}
          </p>
        )}
        <div className={styles.dropdowns}>
          <Toggle
            label="Only show OTAs"
            checked={isOtaOnly}
            handleChange={e => setIsOTAOnly(e.target.checked)}
          />
          {isInternalAdmin && (showAllHotels || showWholeGroup) && (
            <Dropdown
              label="State/Province"
              options={{ '': ' Any state', ...usStates, ...canadianProvinces }}
              value={stateToFilter}
              onChange={e => handleChangeState(e.target.value)}
            />
          )}
          {isInternalAdmin && (showAllHotels || showWholeGroup) && (
            <Dropdown
              label="City"
              options={{
                '': ' Any city',
                ...availableCities
              }}
              value={cityToFilter}
              onChange={e => setCityToFilter(e.target.value)}
              defaultValue="Any city"
            />
          )}
          {isInternalAdmin && (showAllHotels || showWholeGroup) && (
            <Dropdown
              label="Brand"
              options={{
                all: ' Any brand',
                ...parentChainNames
              }}
              value={parentChain}
              onChange={e => setParentChain(e.target.value)}
            />
          )}
          {isInternalAdmin && (
            <Dropdown
              options={{
                currentHotel: '  Current hotel',
                allHotels: ' All hotels',
                ...hotelGroups
              }}
              defaultValue="Any group"
              label="Hotel group & level"
              onChange={e => handleChangeGroupFilter(e.target.value)}
            />
          )}
          <Dropdown
            label="Year"
            className={styles.datepickerDropdown}
            value={activeYear}
            onChange={e => setActiveYear(e.target.value)}
            options={years.reduce((acc, year) => {
              acc[year] = year
              return acc
            }, {})}
          />
        </div>
      </div>
      <div className={styles.chartsHolder} ref={ref}>
        <div className={styles.chartBox}>
          <div className={styles.summaryHolder}>
            <div className={styles.summaryValueHolder}>
              <p className={styles.summaryLabel}>Conversion revenue</p>
              <p className={styles.summaryValue}>
                {currencyFormatter.format(response.totalRevenue)}
              </p>
            </div>
          </div>
          <div style={{ height: 300, width: 'auto', paddingTop: 30 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                onClick={handleBarClick}
                syncId="syncId"
                data={response.monthlyRevenue}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 15
                }}
              >
                <CartesianGrid stroke="#F2F2F2" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                  padding={{ left: 20, right: 20 }}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(0, 14)'
                  }}
                  tickSize={0}
                />
                <YAxis
                  tickSize={0}
                  axisLine={false}
                  allowDecimals={false}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(-2, 0)'
                  }}
                  range
                  tickFormatter={value => currencyFormatter.format(value)}
                />
                <Tooltip
                  content={
                    <RevenueTooltip
                      labelFormatter={label => `${label} ${response.activeYear}`}
                      valueFormatter={value => currencyFormatter.format(value)}
                      dataTitle="Revenue"
                    />
                  }
                  cursor={{
                    stroke: 'red',
                    strokeWidth: 0,
                    fill: '#dedede',
                    opacity: 0.22
                  }}
                />
                {response.activeYear ===
                  moment(response.serviceStartDate).format('YYYY') && (
                  <ReferenceLine
                    x={moment(response.serviceStartDate).format('MMM')}
                    strokeDasharray="3 3"
                    label={CustomLabel}
                    stroke="#2158CF"
                  />
                )}
                <Bar
                  dataKey="value"
                  // fill={colors.revenue}
                  barSize={7}
                >
                  {response.monthlyRevenue.map((entry, index) => (
                    <Cell
                      cursor="pointer"
                      fill={
                        activeMonth
                          ? entry.month === activeMonth
                            ? '#82ca9d'
                            : '#D1EADD'
                          : '#82ca9d'
                      }
                      key={`cell-${index}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className={styles.chartBox}>
          <div className={styles.summaryHolder}>
            <div className={styles.summaryValueHolder}>
              <p className={styles.summaryLabel}>Number of bookings</p>
              <p className={styles.summaryValue}>
                {formatNumberAsThousands(response.totalNumberOfBookings)}
              </p>
            </div>
          </div>
          <div style={{ height: 300, width: 'auto', paddingTop: 30 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                onClick={handleBarClick}
                syncId="syncId"
                data={response.monthlyConversions}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 15
                }}
              >
                <CartesianGrid stroke="#F2F2F2" vertical={false} />
                <XAxis
                  dataKey="month"
                  axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(0, 14)'
                  }}
                  tickSize={0}
                  padding={{ left: 20, right: 20 }}
                />
                <YAxis
                  tickSize={0}
                  axisLine={false}
                  allowDecimals={false}
                  tick={{
                    fill: '#7D7D7D',
                    fontSize: 12,
                    transform: 'translate(-10, 0)'
                  }}
                  range
                />
                <Tooltip
                  content={
                    <RevenueTooltip
                      labelFormatter={label => `${label} ${response.activeYear}`}
                      dataTitle="Bookings"
                    />
                  }
                  cursor={{
                    stroke: 'red',
                    strokeWidth: 0,
                    fill: '#dedede',
                    opacity: 0.22
                  }}
                />
                {response.activeYear ===
                  moment(response.serviceStartDate).format('YYYY') && (
                  <ReferenceLine
                    x={moment(response.serviceStartDate).format('MMM')}
                    strokeDasharray="3 3"
                    label={CustomLabel}
                    stroke="#2158CF"
                  />
                )}
                <Bar
                  dataKey="value"
                  fill={colors.bookings}
                  barSize={7}
                  onClick={handleBarClick}
                >
                  {response.monthlyConversions.map((entry, index) => (
                    <Cell
                      cursor="pointer"
                      fill={
                        activeMonth
                          ? entry.month === activeMonth
                            ? '#667EEA'
                            : '#EBECF1'
                          : '#667EEA'
                      }
                      key={`cell-${index}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <br />
      <OTABookingsList
        isOtaOnly={isOtaOnly}
        month={activeMonth}
        year={response.activeYear}
        kind={kind}
        parentChain={parentChain}
        cityToFilter={cityToFilter}
        groupToFilter={groupToFilter}
        stateToFilter={stateToFilter}
      />
    </div>
  )
}

const CustomLabel = props => {
  return (
    <g>
      <rect
        x={props.viewBox.x - 55}
        y={props.viewBox.y}
        fill="#2158CF"
        width={110}
        height={28}
        rx="15"
      />
      <text
        x={props.viewBox.x}
        y={props.viewBox.y - 4}
        dominant-baseline="middle"
        text-anchor="middle"
        fill="#FFFFFF"
        dy={20}
        dx={0}
        fontSize={12}
      >
        Directful launch
      </text>
    </g>
  )
}

export default MonthlyOTAConversions
